"use client";

import { useState } from "react";
import { ErrorNotice, TextField } from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2 } from "@/components/tool-kit/styles";
import { InflationResults } from "@/components/tools/econ-macro/inflation-results";
import { MACRO_CHART_STRINGS } from "@/components/tools/econ-macro/line-figure";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import {
  CPI_U_FIRST_YEAR,
  CPI_U_LAST_YEAR,
} from "@/lib/tool-kit/econ-macro/cpi-u-annual";
import { coveredYear } from "@/lib/tool-kit/econ-macro/inflation";

/** English strings. A translated content file overrides these through `ui`. */
export const INFLATION_CALCULATOR_STRINGS = {
  ...MACRO_CHART_STRINGS,
  inputsHeading: "Your dollars and years",
  amountField: "Amount in U.S. dollars",
  fromField: "From year",
  toField: "To year",
  yearHint:
    "Whole years from {first} to {last}. You can enter the later year first to look backward in time.",
  dataNote:
    "Uses the U.S. Bureau of Labor Statistics CPI-U (U.S. city average, all items, 1982–84 = 100), annual averages, {first}–{last}.",
  invalidAmount: "Enter an amount of 0 or more.",
  invalidYear: "Enter whole years from {first} to {last}.",
  resultsHeading: "Results",
  summary: "{amount} in {from} has the same buying power as {value} in {to}.",
  valueCard: "Equivalent value in {to}",
  cumulativeCard: "Total inflation, {from} to {to}",
  annualCard: "Average yearly inflation",
  powerCard: "Buying power of the dollar",
  notAvailable: "—",
  workingHeading: "Working",
  workCpi: "CPI-U {from} = {cpiFrom}; CPI-U {to} = {cpiTo}",
  workValue:
    "Equivalent value = amount × CPI({to}) ÷ CPI({from}) = {amount} × {cpiTo} ÷ {cpiFrom} = {value}",
  workCumulative:
    "Total inflation = CPI({to}) ÷ CPI({from}) − 1 = {cpiTo} ÷ {cpiFrom} − 1 = {cumulative}",
  workAnnual:
    "Average yearly rate = (later CPI ÷ earlier CPI)^(1 ÷ {years}) − 1 = {annual}",
  workPower:
    "Buying power = CPI({from}) ÷ CPI({to}) = {cpiFrom} ÷ {cpiTo} = {power}",
  tableSummary: "Show the year-by-year table",
  yearColumn: "Year",
  cpiColumn: "CPI-U (annual average)",
  inflationColumn: "Inflation from the previous year",
  valueColumn: "Value of your amount",
  seriesValue: "Value of your amount",
  chartTitle: "Your amount in each year's dollars",
  chartXTitle: "Year",
  chartYTitle: "Dollars",
  chartDescription:
    "A line graph of {amount} from {from} restated in the dollars of each year from {first} to {last}, ending at {value}.",
  limits:
    "CPI-U measures the average price change for urban consumers as a whole. Your own inflation depends on what you buy, and goods such as housing, tuition or electronics have not followed the average.",
} as const;

export type Strings = typeof INFLATION_CALCULATOR_STRINGS;

export default function InflationCalculatorWidget() {
  const strings = useToolUi(INFLATION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [amountText, setAmountText] = useState("100");
  const [fromText, setFromText] = useState("2000");
  const [toText, setToText] = useState(String(CPI_U_LAST_YEAR));

  const amount = parseNumberInput(amountText);
  const from = parseIntegerInput(fromText);
  const to = parseIntegerInput(toText);
  const limits = {
    first: fmt(CPI_U_FIRST_YEAR, { useGrouping: false }),
    last: fmt(CPI_U_LAST_YEAR, { useGrouping: false }),
  };
  let problem: string | null = null;
  if (amount === null || amount < 0) {
    problem = strings.invalidAmount;
  } else if (
    from === null ||
    to === null ||
    !(coveredYear(from) && coveredYear(to))
  ) {
    problem = t(strings.invalidYear, limits);
  }

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <TextField
            id="inf-amount"
            label={strings.amountField}
            numeric
            onChange={setAmountText}
            value={amountText}
          />
          <TextField
            hint={t(strings.yearHint, limits)}
            id="inf-from"
            label={strings.fromField}
            numeric
            onChange={setFromText}
            value={fromText}
          />
          <TextField
            id="inf-to"
            label={strings.toField}
            numeric
            onChange={setToText}
            value={toText}
          />
        </div>
        <p className="text-muted-foreground text-sm">
          {t(strings.dataNote, limits)}
        </p>
      </section>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
        {problem || amount === null || from === null || to === null ? null : (
          <InflationResults
            amount={amount}
            from={from}
            strings={strings}
            to={to}
          />
        )}
      </section>
    </div>
  );
}
